import { z } from "zod";
import { IdSchema, IsoDateTimeSchema, pageSchema } from "./common.js";
import { CredentialClassSchema, TaskProfileSchema, TaskTypeSchema } from "./provider.js";
import { DevChatCliSchema } from "./runtime.js";

// ---------------------------------------------------------------------------
// Project
// ---------------------------------------------------------------------------

/** Agentry workspace type — Vertical roster template key (Zerovia is source of truth). */
export const WorkspaceTypeSchema = z.enum(["generic", "lp", "app", "business"]);
export type WorkspaceType = z.infer<typeof WorkspaceTypeSchema>;

export const WorkspaceRoleKindSchema = z.enum(["human", "agent"]);
export type WorkspaceRoleKind = z.infer<typeof WorkspaceRoleKindSchema>;

export const WorkspaceRoleSchema = z.object({
	key: z.string().min(1),
	displayName: z.string().min(1),
	kind: WorkspaceRoleKindSchema,
	/** Catalog agent key when kind=agent; null for human roles. */
	agentKey: z.string().min(1).nullable(),
});
export type WorkspaceRole = z.infer<typeof WorkspaceRoleSchema>;

export const WorkspaceTypeDefinitionSchema = z.object({
	type: WorkspaceTypeSchema,
	displayName: z.string().min(1),
	description: z.string(),
	verticalKey: z.string().min(1),
	defaultUcsId: IdSchema,
	roles: z.array(WorkspaceRoleSchema).min(1),
});
export type WorkspaceTypeDefinition = z.infer<typeof WorkspaceTypeDefinitionSchema>;

export const WorkspaceTypeListSchema = z.object({
	items: z.array(WorkspaceTypeDefinitionSchema),
});
export type WorkspaceTypeList = z.infer<typeof WorkspaceTypeListSchema>;

export const ProjectSchema = z.object({
	id: IdSchema,
	tenantId: IdSchema,
	name: z.string().min(1),
	description: z.string(),
	ownerUserId: IdSchema,
	status: z.enum(["ACTIVE", "ARCHIVED"]),
	/** Workspace type selected at create time. */
	workspaceType: WorkspaceTypeSchema,
	/** Resolved Vertical Package key pinned on the Project. */
	verticalKey: z.string().min(1),
	/** Default UCS for sessions under this Project. */
	defaultUcsId: IdSchema,
	createdAt: IsoDateTimeSchema,
});
export type Project = z.infer<typeof ProjectSchema>;

/** Compact Project view returned by the org-scoped external Gateway endpoint. */
export const ProjectListItemSchema = z.object({
	id: IdSchema,
	name: z.string().min(1),
	description: z.string().default(""),
	status: z.enum(["ACTIVE", "ARCHIVED"]),
	isDefault: z.boolean().describe("Derived as true for the oldest ACTIVE Project in the tenant"),
	workspaceType: WorkspaceTypeSchema,
	verticalKey: z.string().min(1),
	/** Default UCS for sessions under this Project (manager hub intake). */
	defaultUcsId: IdSchema,
	createdAt: IsoDateTimeSchema,
});
export type ProjectListItem = z.infer<typeof ProjectListItemSchema>;

export const ProjectListResponseSchema = z.array(ProjectListItemSchema);
export type ProjectListResponse = z.infer<typeof ProjectListResponseSchema>;

export const CreateProjectRequestSchema = z.object({
	name: z.string().min(1).max(120),
	description: z.string().max(2000).default(""),
	/** Defaults to generic so legacy callers keep working; Agentry should set explicitly. */
	workspaceType: WorkspaceTypeSchema.default("generic"),
});
export type CreateProjectRequest = z.infer<typeof CreateProjectRequestSchema>;

// ---------------------------------------------------------------------------
// AgentSession — design §21.4
// ---------------------------------------------------------------------------

export const SessionModeSchema = z.enum(["GUIDED_WORKFLOW", "FREE_CHAT", "ASSIST"]);
export type SessionMode = z.infer<typeof SessionModeSchema>;

export const AgentSessionSchema = z.object({
	id: IdSchema,
	tenantId: IdSchema,
	projectId: IdSchema,
	/** Owns the session AND every artifact generated while owner. Design §21.1. */
	ownerUserId: IdSchema,
	ucsId: IdSchema,
	agentId: IdSchema,
	businessPurpose: z.string(),
	sessionMode: SessionModeSchema,
	title: z.string().min(1),
	status: z.enum(["ACTIVE", "ARCHIVED"]),
	securityClassification: z.enum(["PUBLIC", "INTERNAL", "CONFIDENTIAL"]),
	createdBy: IdSchema,
	createdAt: IsoDateTimeSchema,
	updatedAt: IsoDateTimeSchema,
});
export type AgentSession = z.infer<typeof AgentSessionSchema>;

export const CreateAgentSessionRequestSchema = z.object({
	projectId: IdSchema,
	ucsId: IdSchema,
	title: z.string().min(1).max(160),
	businessPurpose: z.string().max(500).default(""),
	sessionMode: SessionModeSchema.default("GUIDED_WORKFLOW"),
	securityClassification: z.enum(["PUBLIC", "INTERNAL", "CONFIDENTIAL"]).default("INTERNAL"),
});
export type CreateAgentSessionRequest = z.infer<typeof CreateAgentSessionRequestSchema>;

// ---------------------------------------------------------------------------
// SessionAuthRoutingPolicy — design §21.4
// ---------------------------------------------------------------------------

export const SessionAuthCandidateSchema = z.object({
	id: IdSchema,
	routeId: IdSchema,
	authenticationProfileId: IdSchema,
	credentialClass: CredentialClassSchema,
	candidatePriority: z.number().int().min(0),
	status: z.enum(["ACTIVE", "REVOKED"]),
});
export type SessionAuthCandidate = z.infer<typeof SessionAuthCandidateSchema>;

export const SessionProviderRouteSchema = z.object({
	id: IdSchema,
	policyId: IdSchema,
	routeKey: z.string().min(1),
	providerId: IdSchema,
	routePriority: z.number().int().min(0),
	allowedTaskTypes: z.array(TaskTypeSchema),
	status: z.enum(["ACTIVE", "DISABLED"]),
	onRouteExhausted: z.enum(["FALLBACK", "WAIT", "FAIL"]),
	candidates: z.array(SessionAuthCandidateSchema),
});
export type SessionProviderRoute = z.infer<typeof SessionProviderRouteSchema>;

export const SessionAuthRoutingPolicySchema = z.object({
	id: IdSchema,
	sessionId: IdSchema,
	version: z.number().int().min(1),
	selectionMode: z.enum(["EXPLICIT", "AUTO"]),
	status: z.enum(["ACTIVE", "SUPERSEDED"]),
	routes: z.array(SessionProviderRouteSchema),
	createdAt: IsoDateTimeSchema,
});
export type SessionAuthRoutingPolicy = z.infer<typeof SessionAuthRoutingPolicySchema>;

// ---------------------------------------------------------------------------
// Chat
// ---------------------------------------------------------------------------

export const ChatMessageSchema = z.object({
	id: IdSchema,
	sessionId: IdSchema,
	tenantId: IdSchema,
	sequence: z.number().int().min(0),
	role: z.enum(["USER", "AGENT", "SYSTEM"]),
	content: z.string(),
	/** Set on AGENT messages produced by a provider call. */
	providerInvocationId: IdSchema.nullable(),
	routingDecisionId: IdSchema.nullable(),
	executionId: IdSchema.nullable(),
	createdAt: IsoDateTimeSchema,
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

export const SendChatMessageRequestSchema = z.object({
	content: z.string().min(1).max(20000),
	taskProfile: TaskProfileSchema.partial().default({}),
	/** Guided interview answers to hand to the workflow when startWorkflow is true. */
	interviewAnswers: z.record(z.string(), z.string()).optional(),
	/** When true the reply also starts the UCS workflow execution. */
	startWorkflow: z.boolean().default(false),
});
export type SendChatMessageRequest = z.infer<typeof SendChatMessageRequestSchema>;

export const SendChatMessageResponseSchema = z.object({
	userMessage: ChatMessageSchema,
	agentMessage: ChatMessageSchema,
	routingDecisionId: IdSchema.nullable(),
	executionId: IdSchema.nullable(),
});
export type SendChatMessageResponse = z.infer<typeof SendChatMessageResponseSchema>;

const DEV_TRANSCRIPT_OUTPUT_BYTES = 200 * 1024;
const TRUNCATED_PREFIX = "[truncated]";

function truncateDevTranscriptOutput(value: string): string {
	const byteLength = (character: string) => {
		const codePoint = character.codePointAt(0) ?? 0;
		if (codePoint <= 0x7f) return 1;
		if (codePoint <= 0x7ff) return 2;
		if (codePoint <= 0xffff) return 3;
		return 4;
	};
	const characters = [...value];
	if (
		characters.reduce((sum, character) => sum + byteLength(character), 0) <=
		DEV_TRANSCRIPT_OUTPUT_BYTES
	) {
		return value;
	}
	let remaining = DEV_TRANSCRIPT_OUTPUT_BYTES - TRUNCATED_PREFIX.length;
	const tail: string[] = [];
	for (let index = characters.length - 1; index >= 0; index -= 1) {
		const character = characters[index];
		if (character === undefined) continue;
		const length = byteLength(character);
		if (length > remaining) break;
		tail.push(character);
		remaining -= length;
	}
	return `${TRUNCATED_PREFIX}${tail.reverse().join("")}`;
}

export const DevTranscriptRequestSchema = z.object({
	prompt: z.string().min(1).max(32000),
	output: z.string().transform(truncateDevTranscriptOutput),
	cli: DevChatCliSchema,
	runtimeExecId: IdSchema,
	exitCode: z.number().nullable(),
});
export type DevTranscriptRequest = z.infer<typeof DevTranscriptRequestSchema>;

export const DevTranscriptResponseSchema = z.object({
	userMessage: ChatMessageSchema,
	agentMessage: ChatMessageSchema,
});
export type DevTranscriptResponse = z.infer<typeof DevTranscriptResponseSchema>;

export const ProjectPageSchema = pageSchema(ProjectSchema);
export const AgentSessionPageSchema = pageSchema(AgentSessionSchema);
export const ChatMessagePageSchema = pageSchema(ChatMessageSchema);
